/**
 * Shell layout (§18) — branded sidebar + routed outlet. Only managers/admins
 * reach this tree (gated by <RequireRole>); the pack station renders outside it.
 * Sets data-density="compact" (the manager desk) and mounts the ToastProvider so
 * any screen can raise a toast. The pack station will set "comfortable" instead.
 *
 * Task 7 — the nav is grouped into collapsible, icon-led sections that scale as
 * the app grows: **Fulfilment** (Queue · Shipments) and **Settings** (the admin
 * sub-surfaces, which therefore live here rather than in a duplicate sub-nav).
 * Collapse state persists per device; a group that contains the active route is
 * always shown so you can see where you are. Store stays a queue filter (no
 * global store switcher) and order status stays in the queue tabs (not here).
 */
import { useCallback, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSession } from "./auth.js";
import { BackToTop, BrandLockup, Button, Icon, ToastProvider, ThemeToggle } from "./ui/index.js";
import type { IconName } from "./ui/Icon.js";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
}
interface NavGroup {
  id: string;
  label: string;
  icon: IconName;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: "fulfilment",
    label: "Fulfilment",
    icon: "package",
    items: [
      { to: "/queue", label: "Queue", icon: "queue" },
      { to: "/batches", label: "Batches", icon: "layers" },
      { to: "/shipments", label: "Shipments", icon: "truck" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: "sliders",
    items: [
      { to: "/admin/stores", label: "Stores", icon: "store" },
      { to: "/admin/carriers", label: "Carriers", icon: "truck" },
      { to: "/admin/routing", label: "Routing", icon: "route" },
      { to: "/admin/readiness", label: "Readiness", icon: "check" },
      { to: "/admin/trade-lanes", label: "Trade lanes", icon: "globe" },
      { to: "/admin/users", label: "Users", icon: "users" },
      { to: "/admin/stations", label: "Stations", icon: "box" },
      { to: "/admin/barcodes", label: "Barcodes", icon: "search" },
      { to: "/admin/settings", label: "App settings", icon: "sliders" },
    ],
  },
];

const COLLAPSE_KEY = "cairn.nav.collapsed";

function loadCollapsed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(COLLAPSE_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

/** First letters of the first two name words, e.g. "Vasja Bočko" → "VB". */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export function AppLayout() {
  const { user, signOut } = useSession();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);
  const mainRef = useRef<HTMLElement>(null);

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
      } catch {
        /* private mode — keep in-memory only */
      }
      return next;
    });
  }, []);

  return (
    <ToastProvider>
      <div className="app-shell" data-density="compact">
        <nav className="sidebar" aria-label="Primary">
          <div className="sidebar__brand">
            <BrandLockup subtitle="Fulfilment" />
          </div>
          <div className="sidebar__nav">
            {NAV_GROUPS.map((g) => {
              const hasActive = g.items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
              const open = !collapsed.has(g.id) || hasActive;
              return (
                <div key={g.id} className="nav-group">
                  <button
                    type="button"
                    className="nav-group__header"
                    aria-expanded={open}
                    aria-controls={`navgroup-${g.id}`}
                    onClick={() => toggle(g.id)}
                  >
                    <Icon name={g.icon} className="nav-group__icon" />
                    <span className="nav-group__label">{g.label}</span>
                    <Icon name={open ? "chevronDown" : "chevronRight"} className="nav-group__chevron" />
                  </button>
                  {open && (
                    <div className="nav-group__items" id={`navgroup-${g.id}`}>
                      {g.items.map((n) => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          className={({ isActive }) => (isActive ? "nav-link nav-link--active" : "nav-link")}
                        >
                          <Icon name={n.icon} />
                          {n.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="sidebar__spacer" />
          <div className="sidebar__tools">
            <ThemeToggle />
          </div>
          {user && (
            <div className="user-card">
              <div className="user-card__id">
                <span className="user-card__avatar">{initials(user.name)}</span>
                <span className="user-card__meta">
                  <span className="user-card__name truncate">{user.name}</span>
                  <span className="user-card__role">{user.role}</span>
                </span>
              </div>
              <Button variant="secondary" size="sm" icon="logout" block onClick={() => void signOut()}>
                Sign out
              </Button>
            </div>
          )}
        </nav>
        <main className="app-main" ref={mainRef}>
          <Outlet />
          <BackToTop scrollRef={mainRef} />
        </main>
      </div>
    </ToastProvider>
  );
}
